import { z } from '../zod.js';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { BasePageFieldsInputValidationSchema } from '../page/create';
import { withCustomEmailConfirmationValidation } from '../page/common/notification';
import { ThemeOverridesToolInputValidationSchema } from '../theme/themeOverrides';

export const CreateFormPageBaseInputValidationSchema = BasePageFieldsInputValidationSchema.extend({
  title: z.string().max(500).nullish().meta({
    description: 'Public title shown to customers on the page.',
    example: 'Get The Premium Course Today',
  }),
  description: z.string().nullish().meta({
    description: 'Page description shown to customers. Accepts HTML or plain text.',
    example: '<p>Welcome to our <strong>premium</strong> course!</p>',
  }),
  imageIds: z.array(ObjectIdStringValidationSchema).nullish().meta({
    description:
      'Array of images for the page gallery. All referenced file IDs must exist and belong to your account.',
  }),
  fileIds: z
    .array(ObjectIdStringValidationSchema)
    .nullish()
    .meta({
      description:
        'Array of file IDs for downloadable files. All file IDs must exist and belong to your account.',
      example: ['507f1f77bcf86cd799439011', '507f1f77bcf86cd799439012'],
    }),
  formDetails: z
    .object({
      limitSubmissions: z
        .object({
          enabled: z.boolean().nullish().meta({
            description:
              'Prevent a lead who has already submitted this form from submitting again. Defaults to `false`.',
          }),
        })
        .nullish()
        .meta({
          description: 'Duplicate-submission prevention settings for this form.',
        }),
    })
    .nullish()
    .meta({
      description: 'Form behaviour settings.',
    }),
}).meta({ id: 'CreateFormPageBaseInput' });

export const CreateFormPageInputValidationSchema = withCustomEmailConfirmationValidation(
  CreateFormPageBaseInputValidationSchema,
);

// MCP callers cannot set `status` — every page they create goes live immediately.
// `.omit()` throws on a schema that already carries refinements, so it has to run
// against the base schema with the refinement re-applied on top.
// MCP advertises themeOverrides loose; see ThemeOverridesToolInputValidationSchema.
export const CreateFormPageInputWithoutStatusValidationSchema =
  withCustomEmailConfirmationValidation(
    CreateFormPageBaseInputValidationSchema.omit({ status: true }).extend({
      themeOverrides: ThemeOverridesToolInputValidationSchema,
    }),
  );

export type CreateFormPageInput = z.infer<typeof CreateFormPageInputValidationSchema>;
